import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Linkedin, Twitter, ArrowUp, Droplets, Award, Leaf, Shield, TrendingUp, Users, ExternalLink, ChevronRight } from "lucide-react";
import logo from "./favicon.png";

export function Footer() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const footerLinks = {
    company: [
      { name: "About Us", href: "#about" },
      { name: "Mission & Vision", href: "#mission" },
      { name: "Core Values", href: "#values" },
      { name: "Certifications", href: "#certifications" },
    ],
    solutions: [
      { name: "Products & Feedstocks", href: "#products" },
      { name: "B-Trace Platform", href: "#technology" },
      { name: "Collection Process", href: "#process" },
      { name: "Partner With Us", href: "#contact" },
    ],
    resources: [
      { name: "ISCC Certification", href: "#certifications" },
      { name: "Sustainability Reports", href: "#" },
      { name: "Policy Compliance", href: "#" },
      { name: "Downloads", href: "#" },
    ],
  };

  const stats = [
    { icon: Droplets, label: "UCO Collection", value: "Pan-India Network", color: "from-[#1976D2] to-[#1565C0]" },
    { icon: Award, label: "ISCC Certified", value: "Export Ready", color: "from-[#1976D2] to-[#1565C0]" },
    { icon: Shield, label: "100% Traceable", value: "B-Trace IoT", color: "from-[#1976D2] to-[#1565C0]" },
    { icon: TrendingUp, label: "Carbon Reduction", value: "Verified Impact", color: "from-[#1976D2] to-[#1565C0]" },
  ];

  return (
    <footer className="relative bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white overflow-hidden">
      {/* Enhanced Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="footerGrid" width="50" height="50" patternUnits="userSpaceOnUse">
              <path d="M 50 0 L 0 0 0 50" fill="none" stroke="currentColor" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#footerGrid)" />
        </svg>
      </div>

      {/* Animated Gradient Orbs */}
      <motion.div
        animate={{ x: [0, 50, 0], y: [0, -30, 0], scale: [1, 1.2, 1] }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-0 left-0 w-96 h-96 bg-[#1976D2]/20 rounded-full blur-3xl"
      />
      <motion.div
        animate={{ x: [0, -50, 0], y: [0, 30, 0], scale: [1, 1.1, 1] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-0 right-0 w-96 h-96 bg-[#1976D2]/20 rounded-full blur-3xl"
      />

      <div className="relative z-10">
        {/* Premium Stats Banner */}
        <div className="border-b border-white/10 bg-white/5 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group relative"
                >
                  <div className="absolute -inset-1 bg-gradient-to-r from-[#1976D2] to-[#1565C0] rounded-xl opacity-0 group-hover:opacity-50 blur transition-all" />
                  <div className="relative bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-3 sm:p-4 hover:border-[#1976D2]/50 transition-all">
                    <div className="flex items-center gap-3">
                      <div className={`bg-gradient-to-br ${stat.color} w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center shadow-lg flex-shrink-0 group-hover:scale-110 transition-transform`}>
                        <stat.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm sm:text-base font-black text-white truncate">{stat.value}</div>
                        <div className="text-xs text-gray-400 truncate">{stat.label}</div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 lg:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-10 sm:mb-12">
            {/* Company Info - Enhanced */}
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                {/* Logo and Company Name */}
                <div className="flex items-center gap-3 mb-5">
                  <div className="relative">
                    <div className="absolute inset-0 bg-[#1976D2] rounded-xl blur-lg opacity-50" />
                    <img src={logo} alt="KRB Clean Energy" className="relative h-14 sm:h-16 w-auto" />
                  </div>
                  <div>
                    <div className="text-lg sm:text-xl lg:text-2xl font-black text-white leading-tight">
                      KRB Clean Energy Private Limited
                    </div>
                    <div className="text-xs sm:text-sm font-bold bg-gradient-to-r from-[#7CB342] to-[#1976D2] bg-clip-text text-transparent pt-1">
                      Where Waste Becomes Power
                    </div>
                  </div>
                </div>

                <p className="text-sm text-gray-400 leading-relaxed mb-5">
                  Transforming industrial and post-consumer waste into certifiable renewable fuel feedstock. India's most trusted partner for ISCC-certified UCO and renewable feedstock aggregation.
                </p>

                {/* Contact Info - Compact Cards */}
                <div className="space-y-2 mb-6">
                  <div className="bg-white/5 border border-white/10 rounded-lg p-3 hover:border-[#1976D2]/50 transition-all group">
                    <div className="flex items-start gap-2.5">
                      <MapPin className="w-4 h-4 text-[#1976D2] mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform" />
                      <p className="text-xs text-gray-400 leading-relaxed">
                        331/6, 1st Floor, Valayapathy Salai, Mogappair East, Chennai – 600 037
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <a href="tel:+918056801422" className="bg-white/5 border border-white/10 rounded-lg p-3 hover:border-[#1976D2]/50 transition-all group block">
                      <div className="flex items-center gap-2.5">
                        <Phone className="w-4 h-4 text-[#1976D2] flex-shrink-0 group-hover:scale-110 transition-transform" />
                        <span className="text-xs text-gray-400 group-hover:text-white transition-colors">+91 8056801422</span>
                      </div>
                    </a>
                    <a href="mailto:info@krbcleanenergy.com" className="bg-white/5 border border-white/10 rounded-lg p-3 hover:border-[#1976D2]/50 transition-all group block">
                      <div className="flex items-center gap-2.5">
                        <Mail className="w-4 h-4 text-[#1976D2] flex-shrink-0 group-hover:scale-110 transition-transform" />
                        <span className="text-xs text-gray-400 group-hover:text-white transition-colors truncate">info@krbcleanenergy.com</span>
                      </div>
                    </a>
                  </div>
                </div>

                {/* Social Links - Enhanced */}
                <div className="flex gap-3">
                  <button
                    onClick={() => {}}
                    className="relative group"
                  >
                    <div className="absolute -inset-1 bg-gradient-to-r from-[#1976D2] to-[#1565C0] rounded-xl opacity-0 group-hover:opacity-100 blur transition-all" />
                    <div className="relative bg-gradient-to-br from-[#1976D2] to-[#1565C0] text-white w-11 h-11 rounded-xl flex items-center justify-center shadow-lg hover:shadow-2xl transition-all group-hover:scale-110">
                      <Linkedin className="w-5 h-5" />
                    </div>
                  </button>
                  <button
                    onClick={() => {}}
                    className="relative group"
                  >
                    <div className="absolute -inset-1 bg-gradient-to-r from-[#1976D2] to-[#1565C0] rounded-xl opacity-0 group-hover:opacity-100 blur transition-all" />
                    <div className="relative bg-gradient-to-br from-[#1976D2] to-[#1565C0] text-white w-11 h-11 rounded-xl flex items-center justify-center shadow-lg hover:shadow-2xl transition-all group-hover:scale-110">
                      <Twitter className="w-5 h-5" />
                    </div>
                  </button>
                </div>
              </motion.div>
            </div>

            {/* Footer Links - Enhanced Design */}
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
              {/* Company Links */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <div className="mb-4">
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#1976D2]/20 to-transparent border border-[#1976D2]/30 rounded-lg px-3 py-1.5 mb-4">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#1976D2]" />
                    <h4 className="text-sm font-black text-white">Company</h4>
                  </div>
                </div>
                <ul className="space-y-2">
                  {footerLinks.company.map((link, index) => (
                    <li key={index}>
                      <a
                        href={link.href}
                        className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-2 group"
                      >
                        <ChevronRight className="w-3.5 h-3.5 text-[#1976D2] opacity-0 group-hover:opacity-100 -ml-5 group-hover:ml-0 transition-all" />
                        <span className="group-hover:translate-x-1 transition-transform">{link.name}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Solutions Links */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="mb-4">
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#1976D2]/20 to-transparent border border-[#1976D2]/30 rounded-lg px-3 py-1.5 mb-4">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#1976D2]" />
                    <h4 className="text-sm font-black text-white">Solutions</h4>
                  </div>
                </div>
                <ul className="space-y-2">
                  {footerLinks.solutions.map((link, index) => (
                    <li key={index}>
                      <a
                        href={link.href}
                        className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-2 group"
                      >
                        <ChevronRight className="w-3.5 h-3.5 text-[#1976D2] opacity-0 group-hover:opacity-100 -ml-5 group-hover:ml-0 transition-all" />
                        <span className="group-hover:translate-x-1 transition-transform">{link.name}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Resources Links */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <div className="mb-4">
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#1976D2]/20 to-transparent border border-[#1976D2]/30 rounded-lg px-3 py-1.5 mb-4">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#1976D2]" />
                    <h4 className="text-sm font-black text-white">Resources</h4>
                  </div>
                </div>
                <ul className="space-y-2">
                  {footerLinks.resources.map((link, index) => (
                    <li key={index}>
                      <a
                        href={link.href}
                        className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-2 group"
                      >
                        <ChevronRight className="w-3.5 h-3.5 text-[#1976D2] opacity-0 group-hover:opacity-100 -ml-5 group-hover:ml-0 transition-all" />
                        <span className="group-hover:translate-x-1 transition-transform">{link.name}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>

          {/* Newsletter Section - Premium Design */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-10 sm:mb-12"
          >
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-[#1976D2] to-[#1565C0] rounded-2xl opacity-50 group-hover:opacity-75 blur-xl transition-all" />
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-6 sm:p-8 border border-white/20 overflow-hidden">
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-[#1976D2]/20 rounded-full -mr-20 -mt-20 blur-2xl" />
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#1976D2]/20 rounded-full -ml-16 -mb-16 blur-2xl" />
                
                <div className="relative grid lg:grid-cols-2 gap-5 sm:gap-6 items-center">
                  <div>
                    <div className="inline-flex items-center gap-2 bg-[#1976D2]/20 border border-[#1976D2]/40 rounded-full px-4 py-1.5 mb-3">
                      <div className="w-2 h-2 rounded-full bg-[#1976D2] animate-pulse" />
                      <span className="text-xs font-bold text-white">Stay Connected</span>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-black text-white mb-2">
                      Subscribe to Our Newsletter
                    </h3>
                    <p className="text-sm text-gray-300">
                      Get updates on sustainability, policy changes, and clean energy innovation
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="email"
                      placeholder="Your email address"
                      className="flex-1 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-[#1976D2] focus:ring-2 focus:ring-[#1976D2]/50 transition-all"
                    />
                    <button
                      onClick={() => {}}
                      className="relative group/btn"
                    >
                      <div className="absolute -inset-1 bg-gradient-to-r from-[#1976D2] to-[#1565C0] rounded-xl opacity-0 group-hover/btn:opacity-100 blur transition-all" />
                      <div className="relative bg-gradient-to-r from-[#1976D2] to-[#1565C0] text-white px-6 py-3 rounded-xl font-bold text-sm shadow-lg hover:shadow-2xl transition-all whitespace-nowrap flex items-center gap-2">
                        Subscribe
                        <ExternalLink className="w-4 h-4" />
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Bottom Bar - Enhanced */}
          <div className="border-t border-white/10 pt-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-center md:text-left">
                <p className="text-xs text-gray-400">
                  © 2024 <span className="font-bold text-white">KRB Clean Energy Private Limited</span>
                </p>
                <div className="hidden sm:block w-1 h-1 rounded-full bg-gray-600" />
                <p className="text-xs text-gray-500">All rights reserved</p>
              </div>
              <div className="flex items-center gap-4">
                <button className="text-xs text-gray-400 hover:text-white transition-colors">
                  Privacy
                </button>
                <div className="w-1 h-1 rounded-full bg-gray-600" />
                <button className="text-xs text-gray-400 hover:text-white transition-colors">
                  Terms
                </button>
                <div className="w-1 h-1 rounded-full bg-gray-600" />
                <button
                  onClick={scrollToTop}
                  className="relative group/top"
                >
                  <div className="absolute -inset-1 bg-gradient-to-r from-[#1976D2] to-[#1565C0] rounded-lg opacity-0 group-hover/top:opacity-100 blur transition-all" />
                  <div className="relative bg-gradient-to-br from-[#1976D2] to-[#1565C0] text-white w-9 h-9 rounded-lg flex items-center justify-center shadow-lg hover:shadow-2xl transition-all group-hover/top:scale-110">
                    <ArrowUp className="w-4 h-4" />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}